// Libs
import { useContext, useEffect, useMemo, useRef, useState } from "react";

// Local imports
import { AppContext } from "../../../../core/contexts/AppContext";
import "./SelectComponent.scss";

// Types
import { SelectComponentProps, SelectOptionType, handleClick } from "./SelectComponentTypes";

/**
 * @description A custom select component without the <select> HTML tag
 * @param name Name of the component (like an ID)
 * @param data Array with avaiable options
 * @param state String resembling the state of the component (initial, loading, success, error)
 * @param isDisabled Boolean, true if the component should be disabled
 * @param altDefaultOption Modify the default option
 * @param isDefaultSelectable Boolean, true if the default value is selectable
 * @param onSelectOption Callback for the select event
 * @returns The SelectComponent JSX element
 * @description If you need to have the first element from the data param to be default, see the following example
 * @example
 * data={dataArray.slice(1)}
 * isDefaultSelectable={true}
 * altDefaultOption= {
 *     {
 *        value: dataArray[0].value
 *     }}
 * @description or
 * @example altDefaultOption={dataArray[0]} 
 * @description if it matches the SelectOptionType type
 */
const SelectComponent = ({
    name,  data, value,
    state, onSelectOption,
    altDefaultOption = {label: "", value: ""}
}: SelectComponentProps) => {
    const { appText } = useContext(AppContext);
    const { COMPONENTS_TEXT } = appText;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const liRefs = useRef<HTMLLIElement[] | null>([]);

    const stateToText = {
        initial: COMPONENTS_TEXT.SELECT_COMPONENT_NO_DATA_AVAILABLE ,
        loading: COMPONENTS_TEXT.SELECT_COMPONENT_LOADING,
        success: selectedLabel,
        error: COMPONENTS_TEXT.SELECT_COMPONENT_ERROR,
    };

    /**
     * @description A memoized array of options, default option is first
     * Dependencies are defaultOption prop, data prop and appText from context
     */
    const dataMemo = useMemo(() => {
        const defaultOption: SelectOptionType = {
            label: altDefaultOption.label,
            value: altDefaultOption.value
        };

        return [defaultOption, ...data];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [COMPONENTS_TEXT.DROPDOWN_COMPONENT_PLEASE_CHOOSE_ONE_OPTION, altDefaultOption, data]);

    // update selected label on page state reset
    useEffect(() => {
        const newLabel = findLabelByValue();
        setSelectedLabel(newLabel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, data]);

    useEffect(() => {
        liRefs.current?.splice(0, dataMemo?.length);
    }, [dataMemo]);

    /**
     * @description Calls the prop onSelectOption callback and updates the selected label
     * @param option the new option
     */
    const selectOption = (option: SelectOptionType) => {
        value = option.value;
        onSelectOption(name, option.value);
        setSelectedLabel(option.label);
    };

    /**
     * @description Checks if the option is already selected, i.e the values match
     * @param option Checked option
     * @returns Boolean, true if the option's value is the same as the selected one
     */
    const isOptionSelected = (option: SelectOptionType) => {
        return option.value === value;
    };

    /**
     * @description Finds the label in the data options by matching value
     * @returns The label, searched by value
     */
    const findLabelByValue = () => {
        const optionLabel: any = dataMemo.filter((op) => op?.value === value)[0]?.label;
        if (optionLabel === undefined) {
            return;
        }
        return optionLabel;
    };

    /**
     * @description Selects the first option that starts with the inputed letter from the input field
     * and scroll it into view
     * @param e HTML event
     * @returns void if no option starts with the letter
     */
    const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const typedStr = e.currentTarget.value.trim().toLowerCase();
        let newOption = dataMemo.find(x => x.label.toString().toLowerCase().startsWith(typedStr));
        if (!newOption) {
            return;
        }
    
        const indexOfCurrentOption = dataMemo.findIndex(x => x.value === value);
        const optionsAfterCurrent = dataMemo.slice(indexOfCurrentOption + 1);
        const nextOptionWithLetter = optionsAfterCurrent.find(x => x.label.toString().toLowerCase().startsWith(typedStr));
        if (selectedLabel.toString().toLowerCase()[0] === typedStr[0] && nextOptionWithLetter) {
            newOption = nextOptionWithLetter;
        }
    
        selectOption(newOption);
    };

    

    return (
        <div className="SelectComponent">
            <div
                className="SelectComponent__select SelectComponent__select-success"
                id={name}
                tabIndex={0}
                onClick={() => handleClick(inputRef.current, isOpen, setIsOpen)}
                onBlur={() => setIsOpen(false)}
            >
                <input
                    className="SelectComponent__select-valueInput"
                    value={stateToText[state]}
                    ref={inputRef}
                    onInput={handleOnInput}
                    spellCheck={"false"}
                    autoCorrect={"false"}
                ></input>
                <i className="bi bi-chevron-down SelectComponent__select-caret"></i>
                <ul
                    className={`SelectComponent__select__optionsUl ${isOpen ? "SelectComponent__select-show" : ""}`}
                    onMouseDown={(e) => { // prevent select blur when pressing scroll
                        e.preventDefault();
                    }}
                >
                    {dataMemo.map((option, idx) => (
                        <li
                            className={`SelectComponent__select__optionsUl-li ${isOptionSelected(option) ? "SelectComponent__select-selected" : ""} `}
                            key={`${name}-${option.value}-${idx}`}
                            ref={el => {
                                if (liRefs.current && el) {
                                    liRefs.current[idx] = el;
                                }
                            }}
                            onMouseDown={e => { // mouseDown instead of click because mouseDown is before input
                                e.stopPropagation();
                                selectOption(option);
                                setIsOpen(false);
                            }}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default SelectComponent;
